import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material';
import Search from '@mui/icons-material/Search';

import useDebouncer from 'src/common/useDebouncer';
import { useAppDispatch } from 'src/redux-app-hooks';
import { showSnackbar, NotificationSeverity } from 'src/features/notifications/notifications.slice';
import { Owner } from '../crm.dto';
import { admiOwnerService } from '../admi-owner.service';

interface Props {
    value?: Owner | null;
    onSelectOwner: (owner: Owner | null) => void;
    /**
     * Floating MUI label, mirrors how the neighbouring controls
     * (`<Select>` with `<InputLabel>`) align their inputs.
     */
    label?: string;
    size?: 'small' | 'medium';
}

/**
 * Owner = HubSpot user, the actual object behind a customer's
 * `accountManagerId` filter. Lookups go through `/v2/crm/owners/search`,
 * which fetches HubSpot directly (no data-product mirror) and filters
 * client-side on first/last/email.
 */
export function OwnerAutocomplete({ value, onSelectOwner, label, size }: Props) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [owners, setOwners] = useState<Owner[]>([]);
    const [loading, setLoading] = useState(false);
    const debouncer = useDebouncer();
    // Debouncing only cancels pending timers — once a network request is in flight
    // it always runs to completion, so fast typing can let an older response land
    // after a newer one. We stamp every request with the input it was issued for
    // and drop the result if a newer query has been issued meanwhile.
    const latestInputRef = useRef<string | null>(null);

    function handleInputChange(_: unknown, input: string | null, reason: string) {
        // Only search on user typing — selecting an option also fires this
        // with reason='reset', which we don't want to re-fetch on.
        if (reason !== 'input' || !input) return;

        latestInputRef.current = input;
        setLoading(true);
        debouncer(async () => {
            const requestInput = input;
            try {
                const result = await admiOwnerService.searchOwners(input);
                if (latestInputRef.current !== requestInput) return;
                setOwners(result);
            } catch (error) {
                if (latestInputRef.current !== requestInput) return;
                console.error('Failed to search owners:', error);
                dispatch(
                    showSnackbar({
                        message: t('Failed to search account managers. Please try again.'),
                        severity: NotificationSeverity.Error,
                    }),
                );
                setOwners([]);
            } finally {
                if (latestInputRef.current === requestInput) setLoading(false);
            }
        }, 500);
    }

    return (
        <Autocomplete
            options={value && !owners.some((o) => o.id === value.id) ? [value, ...owners] : owners}
            value={value ?? null}
            loading={loading}
            getOptionLabel={(option) => option.displayName}
            isOptionEqualToValue={(option, v) => option.id === v.id}
            onInputChange={handleInputChange}
            onChange={(_, next) => onSelectOwner(next)}
            // Async mode: don't apply the default substring filter to `options`
            // — the server (HubSpot) already filtered them.
            filterOptions={(x) => x}
            size={size}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={t('Search for an account manager')}
                    size={size}
                    slotProps={{
                        ...params.slotProps,
                        ...(label ? { inputLabel: { shrink: true } } : {}),
                        input: {
                            ...params.slotProps.input,
                            startAdornment: (
                                <>
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                    {params.slotProps.input.startAdornment}
                                </>
                            ),
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.slotProps.input.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                    <li key={key || option.id} {...optionProps}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {option.displayName}
                            </Typography>
                            {option.email && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {option.email}
                                </Typography>
                            )}
                        </Box>
                    </li>
                );
            }}
        />
    );
}
